import type {
  ErpGroupHomeworkOverviewQuery,
  ErpLessonHomeworkSubmissionsQuery,
  ErpStudentHomeworkQuery,
} from 'core/graphql';

/**
 * Spec 0012 (homework submissions) — pure model layer for the ERP read-only
 * views: row types re-derived from the generated documents plus the wire-value
 * constants and tone mappers shared by the group/student homework tabs and the
 * LessonsTable «ДЗ» chip. No React/Apollo imports — keep it testable.
 */

export type IHomeworkLessonStats = ErpGroupHomeworkOverviewQuery['groupHomeworkOverviewForErp'][number];

export type IHomeworkRosterRow = ErpLessonHomeworkSubmissionsQuery['lessonHomeworkSubmissionsForErp'][number];

export type IHomeworkSubmission = NonNullable<IHomeworkRosterRow['submission']>;

export type IHomeworkAiReview = NonNullable<IHomeworkSubmission['aiReview']>;

export type IStudentHomeworkRow = ErpStudentHomeworkQuery['studentHomeworkForErp'][number];

/** Structural subset of `LessonHomeworkStatsType` shared with the ride-along
 * `homeworkStats` selection on `GroupLessonsQuery` (no `lessonId` there). */
export interface IHomeworkStatsLike {
  hasAssignment: boolean;
  totalStudents: number;
  submittedCount: number;
  gradedCount: number;
  suspiciousCount: number;
}

/** Assignable to the `_detailShared` `TagTone` union. */
export type HomeworkTagTone = 'ok' | 'warn' | 'danger' | 'neutral' | 'info';

/** `journals.StudentsMark.hw_mark` wire values (plain String on the wire). */
export const HW_MARKS = ['done', 'contains_errors', 'not_done'] as const;
export type HwMark = typeof HW_MARKS[number];

export const isHwMark = (value?: string | null): value is HwMark =>
  value != null && (HW_MARKS as readonly string[]).includes(value);

/** hw_mark → tag tone: done=ok, contains_errors=warn, not_done=danger. */
export const hwMarkTone = (mark?: string | null): HomeworkTagTone => {
  if (mark === 'done') return 'ok';
  if (mark === 'contains_errors') return 'warn';
  if (mark === 'not_done') return 'danger';
  return 'neutral';
};

/** `HomeworkAiReviewType.status` wire values. */
export const AI_STATUSES = [
  'pending',
  'fetching',
  'analyzing',
  'done',
  'failed',
] as const;
export type AiReviewStatus = typeof AI_STATUSES[number];

export const isAiReviewStatus = (
  value?: string | null,
): value is AiReviewStatus =>
  value != null && (AI_STATUSES as readonly string[]).includes(value);

/** AI pipeline status → tag tone (mirrors `recordingTone` in LessonsTable). */
export const aiStatusTone = (status?: string | null): HomeworkTagTone => {
  if (status === 'done') return 'ok';
  if (status === 'failed') return 'danger';
  if (status === 'fetching' || status === 'analyzing') return 'info';
  return 'neutral';
};

/**
 * Tone for the «сдано X/Y» counter chip: everyone submitted = ok, some = warn,
 * none = neutral. Suspicious submissions are flagged by a separate danger
 * icon, not by this tone.
 */
export const submittedTone = (
  submittedCount: number,
  totalStudents: number,
): HomeworkTagTone => {
  if (submittedCount <= 0) return 'neutral';
  if (totalStudents > 0 && submittedCount >= totalStudents) return 'ok';
  return 'warn';
};
